import { PermissionDefinition, PermissionKey, RoleDefinition } from '../models';

export const PERMISSION_CATALOG: PermissionDefinition[] = [
  {
    key: 'users.view',
    label: 'View users',
    description: 'View user profiles and access roles.',
    group: 'User Management',
  },
  {
    key: 'users.invite',
    label: 'Invite users',
    description: 'Invite new users to the organization.',
    group: 'User Management',
  },
  {
    key: 'users.edit',
    label: 'Edit users',
    description: 'Update user roles, profiles, and settings.',
    group: 'User Management',
  },
  {
    key: 'users.disable',
    label: 'Disable users',
    description: 'Deactivate user access to the system.',
    group: 'User Management',
  },
  {
    key: 'users.roles.manage',
    label: 'Manage roles',
    description: 'Create and edit access roles and permissions.',
    group: 'User Management',
  },
  {
    key: 'employees.view',
    label: 'View employees',
    description: 'Read employee profiles and employment details.',
    group: 'Employee Records',
  },
  {
    key: 'employees.edit',
    label: 'Edit employees',
    description: 'Update employee personal and work details.',
    group: 'Employee Records',
  },
  {
    key: 'employees.manage',
    label: 'Manage employees',
    description: 'Create and archive employee records.',
    group: 'Employee Records',
  },
  {
    key: 'departments.manage',
    label: 'Manage departments',
    description: 'Create and update department structures.',
    group: 'Organization',
  },
  {
    key: 'locations.manage',
    label: 'Manage locations',
    description: 'Create and update office locations.',
    group: 'Organization',
  },
  {
    key: 'attendance.view',
    label: 'View attendance',
    description: 'View attendance records and schedules.',
    group: 'Attendance',
  },
  {
    key: 'attendance.manage',
    label: 'Manage attendance',
    description: 'Approve and edit attendance records.',
    group: 'Attendance',
  },
  {
    key: 'leave.view',
    label: 'View leave',
    description: 'View leave balances and requests.',
    group: 'Leave',
  },
  {
    key: 'leave.approve',
    label: 'Approve leave',
    description: 'Approve or reject leave requests.',
    group: 'Leave',
  },
  {
    key: 'leave.manage',
    label: 'Manage leave policies',
    description: 'Configure leave policies and balances.',
    group: 'Leave',
  },
  {
    key: 'expenses.view',
    label: 'View expenses',
    description: 'View expense claims and reimbursement status.',
    group: 'Expenses',
  },
  {
    key: 'expenses.submit',
    label: 'Submit expenses',
    description: 'Create and submit expense claims.',
    group: 'Expenses',
  },
  {
    key: 'expenses.approve',
    label: 'Approve expenses',
    description: 'Approve or reject expense claims.',
    group: 'Expenses',
  },
  {
    key: 'expenses.reimburse',
    label: 'Reimburse expenses',
    description: 'Mark approved expense claims as reimbursed.',
    group: 'Expenses',
  },
  {
    key: 'documents.view',
    label: 'View documents',
    description: 'Access employee and company documents.',
    group: 'Documents',
  },
  {
    key: 'documents.manage',
    label: 'Manage documents',
    description: 'Upload and manage document access.',
    group: 'Documents',
  },
  {
    key: 'approvals.view',
    label: 'View approvals',
    description: 'Review approval workflows and tasks.',
    group: 'Approvals',
  },
  {
    key: 'approvals.manage',
    label: 'Manage approvals',
    description: 'Configure approval workflows.',
    group: 'Approvals',
  },
  {
    key: 'reports.view',
    label: 'View reports',
    description: 'Access analytics and reporting dashboards.',
    group: 'Reports',
  },
  {
    key: 'reports.export',
    label: 'Export reports',
    description: 'Download reports for offline analysis.',
    group: 'Reports',
  },
  {
    key: 'settings.view',
    label: 'View settings',
    description: 'View tenant settings and configurations.',
    group: 'Settings',
  },
  {
    key: 'settings.manage',
    label: 'Manage settings',
    description: 'Update tenant settings and billing.',
    group: 'Settings',
  },
  {
    key: 'audit.view',
    label: 'View audit logs',
    description: 'Review audit activity logs.',
    group: 'Compliance',
  },
  {
    key: 'payroll.view',
    label: 'View own payslips',
    description: 'View personal payslips and pay history.',
    group: 'Payroll',
  },
  {
    key: 'payroll.run',
    label: 'Run payroll',
    description: 'Calculate and process payroll runs.',
    group: 'Payroll',
  },
  {
    key: 'payroll.approve',
    label: 'Approve payroll',
    description: 'Approve payroll runs for payment.',
    group: 'Payroll',
  },
  {
    key: 'payroll.manage',
    label: 'Manage payroll settings',
    description: 'Configure payroll settings, deductions, and benefits.',
    group: 'Payroll',
  },
  {
    key: 'payroll.export',
    label: 'Export payroll data',
    description: 'Download payroll reports and data exports.',
    group: 'Payroll',
  },
  {
    key: 'payroll.viewAll',
    label: 'View all payslips',
    description: 'View payslips for all employees.',
    group: 'Payroll',
  },
];

export const PERMISSION_GROUPS = Array.from(
  new Set(PERMISSION_CATALOG.map((permission) => permission.group))
);

export const SYSTEM_ROLES: RoleDefinition[] = [
  {
    id: 'sys-employee',
    name: 'Employee',
    description: 'Standard employee access to self-service tools.',
    permissions: ['employees.view', 'documents.view', 'leave.view', 'expenses.view', 'expenses.submit', 'payroll.view'],
    isSystem: true,
    baseRole: 'employee',
    createdAt: new Date(),
  },
  {
    id: 'sys-manager',
    name: 'Manager',
    description: 'People manager access to team and approvals.',
    permissions: [
      'employees.view',
      'attendance.view',
      'leave.view',
      'leave.approve',
      'approvals.view',
      'expenses.view',
      'expenses.approve',
    ],
    isSystem: true,
    baseRole: 'manager',
    createdAt: new Date(),
  },
  {
    id: 'sys-hr-admin',
    name: 'HR Admin',
    description: 'Full HR administration and compliance access.',
    permissions: [
      'users.view',
      'users.invite',
      'users.edit',
      'users.disable',
      'users.roles.manage',
      'employees.view',
      'employees.edit',
      'employees.manage',
      'departments.manage',
      'locations.manage',
      'attendance.view',
      'attendance.manage',
      'leave.view',
      'leave.approve',
      'leave.manage',
      'expenses.view',
      'expenses.submit',
      'expenses.approve',
      'expenses.reimburse',
      'documents.view',
      'documents.manage',
      'approvals.view',
      'approvals.manage',
      'reports.view',
      'reports.export',
      'settings.view',
      'audit.view',
      'expenses.view',
      'expenses.reimburse',
      'payroll.view',
      'payroll.run',
      'payroll.approve',
      'payroll.manage',
      'payroll.export',
      'payroll.viewAll',
    ],
    isSystem: true,
    baseRole: 'hr_admin',
    createdAt: new Date(),
  },
  {
    id: 'sys-finance-admin',
    name: 'Finance Admin',
    description: 'Finance oversight with reporting and settings access.',
    permissions: [
      'users.view',
      'reports.view',
      'reports.export',
      'settings.view',
      'audit.view',
      'payroll.view',
      'payroll.approve',
      'payroll.export',
      'payroll.viewAll',
    ],
    isSystem: true,
    baseRole: 'finance_admin',
    createdAt: new Date(),
  },
];

export const PERMISSION_LOOKUP = PERMISSION_CATALOG.reduce(
  (acc, permission) => {
    acc[permission.key] = permission;
    return acc;
  },
  {} as Record<PermissionKey, PermissionDefinition>
);
